/**
 * MCP collection — storefront metafield filters + product grid.
 * Web components: collection-filters, collection-products
 */

function mcpDebounce(fn, wait) {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
  
  function mcpCloseFilterDropdown(dropdown) {
    if (!dropdown) return;
    const trigger = dropdown.querySelector('[data-mcp-filter-dropdown-trigger]');
    const panel = dropdown.querySelector('[data-mcp-filter-dropdown-panel]');
    dropdown.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  }
  
  function mcpCloseAllFilterDropdowns(root, except) {
    if (!root) return;
    root.querySelectorAll('[data-mcp-filter-dropdown].is-open').forEach((dropdown) => {
      if (dropdown !== except) {
        mcpCloseFilterDropdown(dropdown);
      }
    });
  }
  
  function mcpCloseFilterAccordion(accordion) {
    if (!accordion) return;
    const trigger = accordion.querySelector('[data-mcp-filter-accordion-trigger]');
    const panel = accordion.querySelector('[data-mcp-filter-accordion-panel]');
    accordion.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  }

  function mcpOpenFilterAccordion(accordion) {
    if (!accordion) return;
    const trigger = accordion.querySelector('[data-mcp-filter-accordion-trigger]');
    const panel = accordion.querySelector('[data-mcp-filter-accordion-panel]');
    accordion.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (panel) panel.hidden = false;
  }

  function mcpGetMobileFilterAccordionPanel(root) {
    if (!root) return null;
    return root.matches?.('[data-mcp-filters-panel="mobile"]')
      ? root
      : root.querySelector?.('[data-mcp-filters-panel="mobile"]');
  }

  function mcpNormalizeToFirstAccordionOnly(root) {
    const panel = mcpGetMobileFilterAccordionPanel(root);
    if (!panel) return;

    const accordions = [...panel.querySelectorAll('[data-mcp-filter-accordion]')];
    if (!accordions.length) return;

    accordions.forEach((accordion, index) => {
      if (index === 0) {
        mcpOpenFilterAccordion(accordion);
      } else {
        mcpCloseFilterAccordion(accordion);
      }
    });
  }

  function mcpInitFilterAccordions(root, { normalizeInitial = false } = {}) {
    if (!root) return;

    root.querySelectorAll('[data-mcp-filter-accordion]:not([data-mcp-accordion-bound])').forEach((accordion) => {
      accordion.setAttribute('data-mcp-accordion-bound', 'true');
      const trigger = accordion.querySelector('[data-mcp-filter-accordion-trigger]');
      const panel = accordion.querySelector('[data-mcp-filter-accordion-panel]');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const isOpen = accordion.classList.contains('is-open');

        if (isOpen) {
          mcpCloseFilterAccordion(accordion);
        } else {
          mcpOpenFilterAccordion(accordion);
        }
      });
    });

    if (normalizeInitial) {
      mcpNormalizeToFirstAccordionOnly(root);
    }
  }

  function mcpRestoreOpenFilterAccordions(panel, openGroups) {
    if (!panel || !openGroups?.length) return;

    openGroups.forEach((group) => {
      const accordion = panel.querySelector(
        `[data-mcp-filter-accordion][data-mcp-filter-group="${group}"]`
      );
      if (accordion) {
        mcpOpenFilterAccordion(accordion);
      }
    });
  }

  function mcpInitFilterDropdowns(root) {
    if (!root) return;
  
    root.querySelectorAll('[data-mcp-filter-dropdown]:not([data-mcp-dropdown-bound])').forEach((dropdown) => {
      dropdown.setAttribute('data-mcp-dropdown-bound', 'true');
      const trigger = dropdown.querySelector('[data-mcp-filter-dropdown-trigger]');
      const panel = dropdown.querySelector('[data-mcp-filter-dropdown-panel]');
      if (!trigger || !panel) return;
  
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = dropdown.classList.contains('is-open');
        mcpCloseAllFilterDropdowns(root, isOpen ? null : dropdown);
  
        if (isOpen) {
          mcpCloseFilterDropdown(dropdown);
        } else {
          dropdown.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    });
  }
  
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this._onFormInput = mcpDebounce(this._onFormInput.bind(this), 400);
      this._onClick = this._onClick.bind(this);
      this._onPopState = this._onPopState.bind(this);
    }
  
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.collectionProducts = this.closest('collection-products');
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
  
      this.addEventListener('change', this._onFormInput);
      this.addEventListener('click', this._onClick, true);
      window.addEventListener('popstate', this._onPopState);
      document.addEventListener('click', this._onDocumentClick);
      document.addEventListener('keydown', this._onDocumentKeydown);
      mcpInitFilterDropdowns(this);
      mcpInitFilterAccordions(this.querySelector('[data-mcp-filters-panel="mobile"]') || this, {
        normalizeInitial: true,
      });
      this._updateClearAllVisibility();
    }
  
    disconnectedCallback() {
      this.removeEventListener('change', this._onFormInput);
      this.removeEventListener('click', this._onClick, true);
      window.removeEventListener('popstate', this._onPopState);
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onDocumentKeydown);
    }
  
    _onDocumentClick(event) {
      if (!event.target.closest('[data-mcp-filter-dropdown]')) {
        mcpCloseAllFilterDropdowns(this);
      }
    }
  
    _onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        mcpCloseAllFilterDropdowns(this);
      }
    }
  
    _onFormInput(event) {
      if (!event.target.matches('[data-mcp-filter-input]')) return;
      event.stopPropagation();
  
      const pillsGroup = event.target.closest('[data-mcp-filter-pills]');
      if (pillsGroup) {
        this._syncPillStates(pillsGroup);
      }
  
      this.renderFromForms({ updateHistory: true, scroll: true });
      this._updateClearAllVisibility();
    }

    _hasActiveFilters() {
      const params = new URLSearchParams(window.location.search);
      for (const key of params.keys()) {
        if (key.startsWith('filter.')) return true;
      }

      return this.querySelectorAll('[data-mcp-filter-input]:checked').length > 0;
    }

    _updateClearAllVisibility() {
      const hasActive = this._hasActiveFilters();
      this.querySelectorAll('[data-mcp-filter-clear]').forEach((button) => {
        button.classList.toggle('mcp-filter-clear-all--hidden', !hasActive);
        button.disabled = !hasActive;
      });
    }
  
    _syncPillStates(pillsGroup) {
      if (!pillsGroup) return;
      const allBtn = pillsGroup.querySelector('[data-mcp-pill-all]');
      const inputs = pillsGroup.querySelectorAll('[data-mcp-filter-input]');
      const hasActive = Array.from(inputs).some((input) => input.checked);
  
      if (allBtn) {
        allBtn.classList.toggle('is-active', !hasActive);
        allBtn.setAttribute('aria-pressed', !hasActive ? 'true' : 'false');
      }
  
      inputs.forEach((input) => {
        const pill = input.parentElement?.querySelector('.mcp-pill');
        if (pill) {
          pill.classList.toggle('is-active', input.checked);
        }
      });
    }
  
    _onClick(event) {
      const pillAllBtn = event.target.closest('[data-mcp-pill-all]');
      const removeLink = event.target.closest('[data-mcp-filter-remove]');
      const clearLink = event.target.closest('[data-mcp-filter-clear]');
  
      if (pillAllBtn) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const pillsGroup = pillAllBtn.closest('[data-mcp-filter-pills]');
        if (pillsGroup) {
          pillsGroup.querySelectorAll('[data-mcp-filter-input]').forEach((input) => {
            input.checked = false;
          });
          this._syncPillStates(pillsGroup);
        }
        this.renderFromForms({ updateHistory: true, scroll: true });
        return;
      }
  
      if (removeLink && removeLink.href) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const searchParams = this._searchParamsFromUrl(removeLink.href);
        this.renderPage(searchParams, { updateHistory: true, scroll: true });
        return;
      }
  
      if (clearLink) {
        event.preventDefault();
        event.stopImmediatePropagation();
        mcpCloseAllFilterDropdowns(this);
        this.querySelectorAll('[data-mcp-filter-input]').forEach((input) => {
          input.checked = false;
        });
        this.querySelectorAll('[data-mcp-filter-pills]').forEach((pillsGroup) => {
          this._syncPillStates(pillsGroup);
        });
        this.renderPage('', { updateHistory: true, scroll: true });
        this._updateClearAllVisibility();
      }
    }
  
    _onPopState() {
      const searchParams = window.location.search.slice(1);
      this.renderPage(searchParams, { updateHistory: false, scroll: false });
    }
  
    _searchParamsFromUrl(url) {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.delete('section_id');
      return this._mergePreservedParams(parsed.searchParams.toString());
    }

    _getPreservedParams() {
      const preserveKeys = (this.dataset.mcpPreserveParams || 'view')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
      const preserved = new URLSearchParams();
      const current = new URLSearchParams(window.location.search);

      preserveKeys.forEach((key) => {
        if (current.has(key)) {
          preserved.set(key, current.get(key));
        }
      });

      if (!preserved.has('view') && this.dataset.mcpDefaultView) {
        preserved.set('view', this.dataset.mcpDefaultView);
      }

      return preserved;
    }

    _mergePreservedParams(searchParams) {
      const merged = new URLSearchParams(searchParams || '');

      this._getPreservedParams().forEach((value, key) => {
        if (!merged.has(key)) {
          merged.set(key, value);
        }
      });

      return merged.toString();
    }
  
    _getActiveFilterForm() {
      return this.querySelector('#McpFacetFiltersFormDesktop');
    }
  
    _isMobileFilterView() {
      return window.matchMedia('(max-width: 767px)').matches;
    }
  
    _getFilterInputs() {
      if (this._isMobileFilterView()) {
        return [...this.querySelectorAll('[data-mcp-filters-panel="mobile"] [data-mcp-filter-input]')];
      }

      return [
        ...this.querySelectorAll('[data-mcp-filters-panel="desktop-top"] [data-mcp-filter-input]'),
        ...this.querySelectorAll('[data-mcp-filters-panel="tier"] [data-mcp-filter-input]'),
      ];
    }
  
    _createSearchParamsFromForms() {
      const params = new URLSearchParams();
  
      this._getFilterInputs().forEach((input) => {
        if (!input.checked || input.disabled || !input.name) return;
        params.append(input.name, input.value);
      });
  
      return params.toString();
    }
  
    renderFromForms(options) {
      const searchParams = this._createSearchParamsFromForms();
      this.renderPage(searchParams, options);
    }
  
    renderPage(searchParams, { updateHistory = true, scroll = true } = {}) {
      if (this.isLoading || !this.sectionId) return;

      const mergedParams = this._mergePreservedParams(searchParams || '');
      const pathname = window.location.pathname;
      const fetchUrl = new URL(pathname, window.location.origin);

      if (mergedParams) {
        const params = new URLSearchParams(mergedParams);
        params.forEach((value, key) => fetchUrl.searchParams.append(key, value));
      }
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const canonicalUrl = new URL(pathname, window.location.origin);
      if (mergedParams) {
        const params = new URLSearchParams(mergedParams);
        params.forEach((value, key) => canonicalUrl.searchParams.append(key, value));
      }
  
      this._setLoading(true);
  
      fetch(fetchUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          if (this.collectionProducts) {
            this.collectionProducts.updateFromHtml(html);
          }
  
          if (updateHistory) {
            const displayUrl = canonicalUrl.search ? canonicalUrl.toString() : pathname;
            window.history.pushState({ mcpFacets: true, searchParams: mergedParams }, '', displayUrl);
          }
  
          if (scroll && this.collectionProducts) {
            this.collectionProducts.scrollToGrid();
          }
        })
        .catch((error) => {
          console.error('Collection filter fetch error:', error);
          window.location.href = canonicalUrl.search ? canonicalUrl.toString() : pathname;
        })
        .finally(() => {
          this._setLoading(false);
          this._updateClearAllVisibility();
        });
    }
  
    _setLoading(loading) {
      this.isLoading = loading;
      if (this.collectionProducts) {
        this.collectionProducts.setLoading(loading);
      }
    }
  }
  
  class CollectionProducts extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onOpenClick = this._onOpenClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onApplyClick = this._onApplyClick.bind(this);
      this._onPaginationClick = this._onPaginationClick.bind(this);
    }
  
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.drawer = this.querySelector('[data-mcp-drawer]');
      this.openBtn = this.querySelector('[data-mcp-filter-open]');
      this.closeBtns = this.querySelectorAll('[data-mcp-filter-close]');
      this.applyBtn = this.querySelector('[data-mcp-filter-apply]');
      this.overlay = this.querySelector('[data-mcp-drawer-overlay]');
      this.productGrid = this.querySelector('[data-mcp-product-grid]');
      this.loadingEl = this.querySelector('[data-mcp-loading]');
      this.collectionFilters = this.querySelector('collection-filters');
  
      if (this.openBtn) {
        this.openBtn.addEventListener('click', this._onOpenClick);
      }
  
      this.closeBtns.forEach((btn) => {
        btn.addEventListener('click', this._onCloseClick);
      });
  
      if (this.applyBtn) {
        this.applyBtn.addEventListener('click', this._onApplyClick);
      }
  
      if (this.overlay) {
        this.overlay.addEventListener('click', this._onOverlayClick);
      }
  
      this.addEventListener('click', this._onPaginationClick, true);
      this._initVariantSelects();
    }
  
    disconnectedCallback() {
      if (this.openBtn) {
        this.openBtn.removeEventListener('click', this._onOpenClick);
      }
  
      this.closeBtns.forEach((btn) => {
        btn.removeEventListener('click', this._onCloseClick);
      });
  
      if (this.applyBtn) {
        this.applyBtn.removeEventListener('click', this._onApplyClick);
      }
  
      if (this.overlay) {
        this.overlay.removeEventListener('click', this._onOverlayClick);
      }
  
      this.removeEventListener('click', this._onPaginationClick, true);
      document.removeEventListener('keydown', this._onKeydown);
    }
  
    _onPaginationClick(event) {
      const paginationLink = event.target.closest('[data-mcp-pagination] a');
      if (!paginationLink || !paginationLink.href) return;
  
      event.preventDefault();
      event.stopImmediatePropagation();
  
      if (this.collectionFilters) {
        const paginationParams = new URL(paginationLink.href, window.location.origin).searchParams;
        paginationParams.delete('section_id');
        this.collectionFilters.renderPage(paginationParams.toString(), {
          updateHistory: true,
          scroll: true,
        });
      }
    }
  
    _getSourceRoot(doc) {
      return (
        doc.querySelector('collection-products') ||
        doc.getElementById(`shopify-section-${this.sectionId}`) ||
        doc.body
      );
    }
  
    updateFromHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = this._getSourceRoot(doc);
  
      const newGridInner = source.querySelector('[data-mcp-product-grid-inner]');
      const currentGridInner = this.querySelector('[data-mcp-product-grid-inner]');
      if (newGridInner && currentGridInner) {
        currentGridInner.innerHTML = newGridInner.innerHTML;
      }

      const newPagination = source.querySelector('[data-mcp-pagination]');
      const currentPagination = this.querySelector('[data-mcp-pagination]');
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      }
  
      const currentMobilePanel = this.querySelector('[data-mcp-filters-panel="mobile"]');
      const openAccordionGroups = currentMobilePanel
        ? [...currentMobilePanel.querySelectorAll('[data-mcp-filter-accordion].is-open')]
            .map((accordion) => accordion.getAttribute('data-mcp-filter-group'))
            .filter(Boolean)
        : [];

      ['desktop-top', 'tier', 'mobile'].forEach((panel) => {
        const newPanel = source.querySelector(`[data-mcp-filters-panel="${panel}"]`);
        const currentPanel = this.querySelector(`[data-mcp-filters-panel="${panel}"]`);
        if (newPanel && currentPanel) {
          currentPanel.innerHTML = newPanel.innerHTML;
        }
      });
  
      if (this.collectionFilters) {
        mcpInitFilterDropdowns(this.collectionFilters);
        const mobilePanel =
          this.collectionFilters.querySelector('[data-mcp-filters-panel="mobile"]') || this.collectionFilters;
        mcpInitFilterAccordions(mobilePanel);
        mcpRestoreOpenFilterAccordions(mobilePanel, openAccordionGroups);
      }
  
      const newCount = source.querySelector('[data-mcp-filter-count]');
      const currentCount = this.querySelector('[data-mcp-filter-count]');
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
        const isHidden = newCount.classList.contains('mcp-filter-trigger__count--hidden');
        currentCount.classList.toggle('mcp-filter-trigger__count--hidden', isHidden);
        currentCount.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
      }
  
      const newResultsLabel = source.querySelector('[data-mcp-results-label]');
      const currentResultsLabel = this.querySelector('[data-mcp-results-label]');
      if (newResultsLabel && currentResultsLabel) {
        currentResultsLabel.innerHTML = newResultsLabel.innerHTML;
      }
  
      this._initVariantSelects();
      this._refreshThirdPartyWidgets();

      if (this.collectionFilters) {
        this.collectionFilters._updateClearAllVisibility();
      }
    }
  
    _getScrollOffset() {
      let offset = 24;

      const header = document.querySelector('.site-header');
      if (header) {
        offset += header.getBoundingClientRect().height;
      }

      const announcement = document.querySelector('.announcement-bar, .announcement');
      if (announcement) {
        const style = window.getComputedStyle(announcement);
        if (style.display !== 'none' && style.visibility !== 'hidden') {
          offset += announcement.getBoundingClientRect().height;
        }
      }

      return offset;
    }

    scrollToGrid() {
      if (!this.productGrid) return;

      requestAnimationFrame(() => {
        const offset = this._getScrollOffset();
        const top = this.productGrid.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }
  
    setLoading(loading) {
      if (this.productGrid) {
        this.productGrid.classList.toggle('is-loading', loading);
      }
      if (this.loadingEl) {
        this.loadingEl.hidden = !loading;
      }
      this.querySelectorAll('[data-mcp-filter-input], [data-mcp-pill-all], [data-mcp-pagination] a').forEach((el) => {
        el.setAttribute('aria-disabled', loading ? 'true' : 'false');
      });
    }
  
    _refreshThirdPartyWidgets() {
      const refreshYotpo = () => {
        if (window.yotpoWidgetsContainer?.initWidgets) {
          window.yotpoWidgetsContainer.initWidgets();
        }

        if (window.yotpo) {
          try {
            if (window.Yotpo?.API) {
              new window.Yotpo.API(window.yotpo).refreshWidgets();
            } else if (typeof window.yotpo.refreshWidgets === 'function') {
              window.yotpo.refreshWidgets();
            }
          } catch (error) {
            if (typeof window.yotpo.refreshWidgets === 'function') {
              window.yotpo.refreshWidgets();
            }
          }
        }
      };

      refreshYotpo();
      requestAnimationFrame(refreshYotpo);
      window.setTimeout(refreshYotpo, 150);
      window.setTimeout(refreshYotpo, 600);

      this.dispatchEvent(new CustomEvent('collection-products:updated', { bubbles: true }));
    }
  
    openDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.openBtn) {
        this.openBtn.setAttribute('aria-expanded', 'true');
      }
      document.body.classList.add('mcp-drawer-open');
      document.addEventListener('keydown', this._onKeydown);
      const closeBtn = this.drawer.querySelector('[data-mcp-filter-close]');
      if (closeBtn) closeBtn.focus();
    }
  
    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.openBtn) {
        this.openBtn.setAttribute('aria-expanded', 'false');
      }
      document.body.classList.remove('mcp-drawer-open');
      document.removeEventListener('keydown', this._onKeydown);
    }
  
    _onOpenClick(event) {
      event.preventDefault();
      this.openDrawer();
    }
  
    _onCloseClick(event) {
      event.preventDefault();
      this.closeDrawer();
    }
  
    _onApplyClick(event) {
      event.preventDefault();
      this.closeDrawer();
    }
  
    _onOverlayClick(event) {
      event.preventDefault();
      this.closeDrawer();
    }
  
    _onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeDrawer();
      }
    }
  
    _initVariantSelects() {
      const selectOptions = this.querySelectorAll('.product-variant-select:not([data-mcp-bound])');
  
      selectOptions.forEach((select) => {
        select.setAttribute('data-mcp-bound', 'true');
        select.addEventListener('change', function onVariantChange() {
          const selectedOption = this.options[this.selectedIndex];
          const dataPrice = selectedOption.getAttribute('data-price');
          const comparePrice = selectedOption.getAttribute('data-comparedprice');
          const featuredImg = selectedOption.getAttribute('data-image');
          const mainParent = this.closest('.product-card');
          if (!mainParent) return;
  
          const imageChange = mainParent.querySelector('.product-image img');
          const productAvailable = selectedOption.getAttribute('data-available');
          const productBtn = mainParent.querySelector('.btn');
          const regularPrice = mainParent.querySelector('.price-item--regular');
          const productPriceWrap = mainParent.querySelector('.product-price');
          const isValidPrice = function (value) {
            if (value == null) return false;
            var normalized = String(value).trim().toLowerCase();
            return (
              normalized !== '' &&
              normalized !== 'null' &&
              normalized !== 'undefined' &&
              normalized !== '$0.00'
            );
          };
  
          if (regularPrice) {
            if (!isValidPrice(dataPrice) && !isValidPrice(comparePrice)) {
              regularPrice.innerHTML = '';
              if (productPriceWrap) productPriceWrap.style.display = 'none';
            } else if (isValidPrice(comparePrice) && comparePrice !== '$0.00') {
              if (productPriceWrap) productPriceWrap.style.display = '';
              regularPrice.innerHTML =
                '<span class="price-item custom-sale price-item--sale">' +
                comparePrice +
                '</span>' +
                (isValidPrice(dataPrice) ? dataPrice : '');
            } else if (isValidPrice(dataPrice)) {
              if (productPriceWrap) productPriceWrap.style.display = '';
              regularPrice.innerHTML = dataPrice;
            } else {
              regularPrice.innerHTML = '';
              if (productPriceWrap) productPriceWrap.style.display = 'none';
            }
          }
  
          if (imageChange && featuredImg) {
            imageChange.setAttribute('src', featuredImg);
          }
  
          if (productBtn) {
            if (productAvailable === 'false') {
              productBtn.innerHTML = 'Sold Out';
              productBtn.disabled = true;
            } else {
              productBtn.innerHTML = 'Add To Cart';
              productBtn.disabled = false;
            }
          }
        });
      });
    }
  }
  
  if (!customElements.get('collection-filters')) {
    customElements.define('collection-filters', CollectionFilters);
  }
  
  if (!customElements.get('collection-products')) {
    customElements.define('collection-products', CollectionProducts);
  }